/**
 * Feedback API — mirrors smt_feedback_handler.go.
 *
 *   POST  /api/v1/smt/feedback              Create (auth optional)
 *   GET   /api/v1/smt/admin/feedback        AdminList (admin)
 *   PUT   /api/v1/smt/admin/feedback/:id    AdminHandle (admin)
 */
import { request, requestData } from './client.ts';

export type FeedbackStatus = 'new' | 'in_progress' | 'done' | 'archived';

export interface Feedback {
  id: number;
  message: string;
  lang: 'fi' | 'en';
  status: FeedbackStatus;
  created_at: string;
  submitter_user_id?: number | null;
  submitter_name?: string | null;
  submitter_email?: string | null;
  context?: string | null;
  handled_at?: string | null;
  handled_by?: number | null;
  handled_note?: string | null;
}

export interface SubmitFeedbackInput {
  message: string;
  lang?: 'fi' | 'en';
  name?: string;
  email?: string;
  context?: string; // serialised JSON
}

export async function submitFeedback(input: SubmitFeedbackInput): Promise<{ id: number; status: FeedbackStatus }> {
  // Anonymous submissions skip the 401 redirect; even when a session is
  // available we keep the redirect off so an expired token doesn't blow up
  // a successful submission flow.
  const wrapped = await request<{ data: { id: number; status: FeedbackStatus }; message: string }>(
    '/smt/feedback',
    { method: 'POST', body: input, skipAuthRedirect: true },
  );
  return wrapped.data;
}

// --- Admin ---

export async function adminListFeedback(status?: FeedbackStatus | 'all'): Promise<Feedback[]> {
  const query: Record<string, string> = {};
  if (status && status !== 'all') query.status = status;
  return (await requestData<Feedback[] | null>('/smt/admin/feedback', { query })) ?? [];
}

export async function adminHandleFeedback(id: number, status: FeedbackStatus, note?: string): Promise<Feedback> {
  return requestData<Feedback>(`/smt/admin/feedback/${id}`, {
    method: 'PUT',
    body: { status, note: note || '' },
  });
}
